/**
 * ReportIssueButton — Floating button "Zglos problem" w prawym dolnym rogu.
 * Zastepuje stary VoiceAssistant w AppShell.
 *
 * Modal zbiera: tytul, opis, kategoria, severity, moduł (auto-detect z URL).
 * Auto-context: URL, user_agent, recent actions.
 */

import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bug, Loader2, CheckCircle2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { submitReport, attachReportFile } from "@/lib/api";
import { humanizeApiError } from "@/lib/errors";

// Kategorie i priorytety sa zdefiniowane inline w Select (unikamy unused var warnings)

// Zalaczniki — limity zgodne z backendem (POST /reports/{id}/attach)
const MAX_FILES = 5;
const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MB
const ALLOWED_EXTENSIONS = ["pdf", "docx", "doc", "odt", "txt", "rtf", "png", "jpg", "jpeg", "webp", "heic"];

function fileExtension(name) {
  const parts = (name || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Wyciagnij project_id z URL gdy jestesmy w kontekscie projektu (/project/{id}/...)
function detectProjectId(pathname) {
  const m = (pathname || "").match(/\/project\/([^/]+)/);
  return m ? m[1] : null;
}

// Auto-detect module from URL
function detectModule(pathname) {
  if (pathname.includes("/advice")) return "advice";
  if (pathname.includes("/creator")) return "creator";
  if (pathname.includes("/contracts")) return "contracts";
  if (pathname.includes("/studio")) return "studio";
  if (pathname.includes("/legal-search") || pathname.includes("/orzecznictwo")) return "legal_search";
  if (pathname.includes("/prediction") || pathname.includes("/predykcja")) return "prediction";
  if (pathname.includes("/agent")) return "agent";
  if (pathname.includes("/court-portal") || pathname.includes("/portal")) return "court_portal";
  if (pathname.includes("/negotiation") || pathname.includes("/negocjacje")) return "negotiation";
  if (pathname.includes("/due-diligence") || pathname.includes("/dd")) return "due_diligence";
  if (pathname.includes("/compliance") || pathname.includes("/rodo")) return "compliance";
  if (pathname.includes("/templates")) return "templates";
  if (pathname.includes("/calculators") || pathname.includes("/kalkulatory")) return "calculators";
  if (pathname.includes("/settings") || pathname.includes("/ustawienia")) return "settings";
  if (pathname.includes("/knowledge")) return "knowledge_base";
  if (pathname === "/" || pathname.includes("/dashboard")) return "dashboard";
  if (pathname.includes("/admin")) return "admin";
  if (pathname.includes("/pdf")) return "pdf_lab";
  return "other";
}

export default function ReportIssueButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "bug",
    severity: "medium",
    what_wrong: "",
    expected: "",
  });
  const [files, setFiles] = useState([]);

  const handleOpen = () => {
    setOpen(true);
    setSubmitted(false);
    setForm({ title: "", description: "", category: "bug", severity: "medium", what_wrong: "", expected: "" });
    setFiles([]);
  };

  const handleFilesSelected = (e) => {
    const picked = Array.from(e.target.files || []);
    // Reset input value, zeby ten sam plik mozna bylo wybrac ponownie po usunieciu
    e.target.value = "";
    if (picked.length === 0) return;

    setFiles((prev) => {
      const next = [...prev];
      for (const f of picked) {
        const ext = fileExtension(f.name);
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
          toast.error(`Niedozwolony typ pliku: ${f.name} (dozwolone: ${ALLOWED_EXTENSIONS.join(", ")})`);
          continue;
        }
        if (f.size > MAX_FILE_BYTES) {
          toast.error(`Plik za duzy: ${f.name} (max 25 MB)`);
          continue;
        }
        if (next.length >= MAX_FILES) {
          toast.error(`Maksymalnie ${MAX_FILES} plikow na zgloszenie`);
          break;
        }
        // Deduplikacja po nazwie+rozmiarze (proste, wystarczajace)
        if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
        next.push(f);
      }
      return next;
    });
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (form.title.length < 3) {
      toast.error("Tytul musi miec minimum 3 znaki");
      return;
    }
    if (form.description.length < 10) {
      toast.error("Opis musi miec minimum 10 znakow");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        what_wrong: form.what_wrong.trim() || null,
        expected: form.expected.trim() || null,
        project_id: detectProjectId(location.pathname),
        module: detectModule(location.pathname),
        context: {
          url: window.location.href,
          pathname: location.pathname,
          screen: `${window.screen.width}x${window.screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          timestamp_local: new Date().toISOString(),
        },
      };
      const res = await submitReport(payload);
      const reportId = res.data?.id;

      // Warstwa 3: zalaczniki — jedno wywolanie na plik, sekwencyjnie.
      // Blad pojedynczego pliku nie blokuje reszty ani sukcesu zgloszenia.
      if (reportId && files.length > 0) {
        let failed = 0;
        for (const f of files) {
          try {
            await attachReportFile(reportId, f);
          } catch (attachErr) {
            failed += 1;
            toast.error(`Plik „${f.name}": ${humanizeApiError(attachErr, "dołączyć załącznik")}`);
          }
        }
        if (failed > 0 && failed < files.length) {
          toast.warning(`Dolaczono ${files.length - failed} z ${files.length} plikow`);
        }
      }

      setSubmitted(true);
      toast.success(res.data?.message || "Zgloszenie przyjete");
      setTimeout(() => setOpen(false), 2500);
    } catch (e) {
      toast.error(humanizeApiError(e, "wysłać zgłoszenie"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating button — prawy dolny róg */}
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group"
        title="Zglos problem lub propozycje"
        aria-label="Zglos problem"
      >
        <Bug className="h-6 w-6 group-hover:scale-110 transition-transform" />
      </button>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-primary" />
              Zglos problem
            </DialogTitle>
            <DialogDescription>
              Opisz krotko co nie dziala albo co chcialbys zeby zostalo dodane.
              Zespol otrzyma Twoje zgloszenie i zajmie sie nim.
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="py-8 flex flex-col items-center gap-3 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
              <p className="text-lg font-medium">Zgloszenie przyjete!</p>
              <p className="text-sm text-muted-foreground">
                Dziekujemy. Mozesz sledzic status w "Moje zgloszenia".
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Tytul *</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Np. Kreator nie zapisuje dokumentu"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Opis *</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Co probowales zrobic? Co sie stalo? Jaki blad widzisz?"
                  rows={5}
                  maxLength={5000}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {form.description.length}/5000 znakow
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Co konkretnie jest zle?</label>
                <Textarea
                  value={form.what_wrong}
                  onChange={(e) => setForm({ ...form, what_wrong: e.target.value })}
                  placeholder="Np. wygenerowane pismo pomija zarzut przedawnienia, ktory podalem w materialach"
                  rows={3}
                  maxLength={3000}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Czego oczekiwales?</label>
                <Textarea
                  value={form.expected}
                  onChange={(e) => setForm({ ...form, expected: e.target.value })}
                  placeholder="Np. pismo powinno zawierac wniosek o oddalenie powodztwa z uwagi na przedawnienie"
                  rows={3}
                  maxLength={3000}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Zalaczniki (opcjonalnie)</label>
                <label className="flex items-center gap-2 cursor-pointer text-sm border border-dashed rounded-lg px-3 py-2 hover:bg-muted/50 transition-colors">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Dodaj pliki (max {MAX_FILES}, do 25 MB)</span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.docx,.doc,.odt,.txt,.rtf,.png,.jpg,.jpeg,.webp,.heic"
                    className="hidden"
                    onChange={handleFilesSelected}
                    disabled={files.length >= MAX_FILES}
                  />
                </label>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <li
                        key={`${f.name}-${f.size}-${i}`}
                        className="flex items-center justify-between gap-2 text-xs bg-muted/40 rounded px-2 py-1"
                      >
                        <span className="truncate flex-1">{f.name}</span>
                        <span className="text-muted-foreground shrink-0">{formatBytes(f.size)}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                          title="Usun plik"
                          aria-label={`Usun ${f.name}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Dozwolone: PDF, DOCX, DOC, ODT, TXT, RTF, PNG, JPG, WEBP, HEIC.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Kategoria</label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bug">Blad</SelectItem>
                      <SelectItem value="feature_request">Propozycja funkcji</SelectItem>
                      <SelectItem value="performance">Wydajnosc</SelectItem>
                      <SelectItem value="ui_ux">Interfejs / UX</SelectItem>
                      <SelectItem value="data_issue">Problem z danymi</SelectItem>
                      <SelectItem value="login_issue">Logowanie</SelectItem>
                      <SelectItem value="payment">Platnosci</SelectItem>
                      <SelectItem value="other">Inne</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Priorytet</label>
                  <Select
                    value={form.severity}
                    onValueChange={(v) => setForm({ ...form, severity: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Niski</SelectItem>
                      <SelectItem value="medium">Sredni</SelectItem>
                      <SelectItem value="high">Wysoki</SelectItem>
                      <SelectItem value="critical">Krytyczny</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
                Automatycznie dolaczamy: aktualna strona ({detectModule(location.pathname)}), przegladarka, rozdzielczosc
                {detectProjectId(location.pathname) ? " oraz kontekst generacji biezacego projektu (do diagnozy)" : ""}.
                Nie zbieramy hasel.
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
                  Anuluj
                </Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Wysylanie...
                    </>
                  ) : (
                    "Wyslij zgloszenie"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
